import { Button, MessageBar, MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { resyncReleases } from '../../api/releases';
import { describeSync, hasSyncWarnings, releaseErrorMessage, type SyncWarnings } from '../../releases/releaseChange';
import { useReleaseMutation } from './useReleases';

/**
 * Non-blocking warnings after a release change: the deployment manifest and/or Guardian did not
 * take the change. The change itself is saved. Resync repairs both.
 */
export function SyncWarningBar({ productId, warnings, onResynced }: {
  productId: string;
  warnings: SyncWarnings;
  /** Called with the warnings left after a resync (none when it fully worked), and a message. */
  onResynced: (left: SyncWarnings, message: string, ok: boolean) => void;
}) {
  const resync = useReleaseMutation(productId, () => resyncReleases(productId));
  const run = () => resync.mutate(undefined, {
    onSuccess: (r) => {
      const left = describeSync(r);
      const ok = !hasSyncWarnings(left);
      onResynced(left, ok ? 'Resynced: the deployment manifest and Guardian are up to date.' : 'Resync finished, but not everything is in step yet.', ok);
    },
    onError: (e) => onResynced(warnings, releaseErrorMessage(e), false),
  });
  const action = (
    <MessageBarActions>
      <Button size="small" onClick={run} disabled={resync.isPending}>{resync.isPending ? 'Resyncing…' : 'Resync'}</Button>
    </MessageBarActions>
  );
  const g = warnings.guardian;
  return (
    <>
      {warnings.manifest && (
        <MessageBar intent="warning" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>Deployment manifest not updated</MessageBarTitle>
            {warnings.manifest}
          </MessageBarBody>
          {action}
        </MessageBar>
      )}
      {g && (
        <MessageBar intent="warning" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>Guardian has not received the latest release data</MessageBarTitle>
            The change was saved here. {g.error ? `Guardian said: ${g.error}. ` : ''}
            {g.notSentCount > 0 ? `${g.notSentCount} release${g.notSentCount === 1 ? ' was' : 's were'} not sent. ` : ''}
            Installs will not show the new version until it is resynced.
          </MessageBarBody>
          {!warnings.manifest && action}
        </MessageBar>
      )}
    </>
  );
}
